/**
 * Il messaggio da mostrare sotto un campo che la validazione ha rifiutato.
 *
 * **Perche' non basta mostrare quello che manda l'api.** L'api e' Lumen senza
 * traduzioni — non ha `resources/lang` e non imposta il locale sulla richiesta —
 * quindi i suoi messaggi escono sempre in inglese: «The quantity must be at
 * least 0.». Metterli sotto il campo e' meglio del silenzio, ma a un utente che
 * sta usando l'applicazione in italiano dice poco.
 *
 * Qui il messaggio si prende dalle traduzioni locali quando il campo ne ha una,
 * e si ricade su quello dell'api quando non ce l'ha: un campo che nessuno ha
 * ancora tradotto mostra comunque il motivo del rifiuto, invece di sparire.
 *
 * **La chiave e' il campo, non il tipo di vincolo.** L'api non manda un codice
 * per distinguere `required` da `min` da `max`, manda solo la prosa inglese, e
 * dedurre il vincolo dal testo sarebbe un accoppiamento a una stringa che non
 * controlliamo. Il messaggio tradotto descrive quindi **tutto** il vincolo del
 * campo — «un numero maggiore o uguale a zero» copre sia il tipo sia il minimo —
 * che e' cio' che serve a chi deve correggere. Se un giorno l'api mandera'
 * codici stabili, questa mappa potra' diventare piu' fine senza cambiare i
 * chiamanti.
 *
 * **Le chiavi sono per campo e valgono per tutta l'applicazione**, non per il
 * form che le usa: `name` e' il nome di un prodotto qui, di una persona nel
 * profilo, di un'azienda in registrazione. Un messaggio che nomina l'entita'
 * sarebbe quindi sbagliato altrove, e i testi restano neutri — «Inserisci un
 * nome», non «il nome del prodotto». Dove servisse davvero distinguere, la
 * chiave andra' resa piu' specifica insieme al chiamante, non qui.
 */

/**
 * `null` quando il campo non ha errori.
 *
 * `errors` e' la mappa `{campo: [messaggi]}` che l'interceptor deposita nello
 * store — gia' sciolta dal suo involucro, vedi la nota in `useApi`.
 *
 * `translate` e' la funzione `t` di chi chiama, con il suo locale gia' legato:
 * questo modulo non sa niente di i18n e resta provabile senza montare un
 * componente. Quando la chiave non esiste, vue-i18n restituisce **la chiave
 * stessa**: e' il segnale che non c'e' traduzione e che si deve ricadere
 * sull'api.
 */
export const fieldErrorMessage = (field, errors, translate) =>
{
    const messages = errors?.[field];

    if(!Array.isArray(messages) || messages.length === 0)
    {
        // Una stringa al posto dell'array non e' la forma che l'api manda, ma
        // arriva dalle guardie locali che scrivono nello stesso store: vale
        // come messaggio gia' pronto.
        return typeof messages === 'string' && messages !== '' ? messages : null;
    }

    const key = `validation.fields.${field}`;
    const translated = translate(key);

    return translated && translated !== key ? translated : messages[0];
};
